/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import * as Yup from 'yup';

const nameValidation = (
  max: number = Infinity,
  matches: RegExp = /.*/,
  required: boolean = false,
  message: string = "name"
) =>
  Yup.string()
    .max(max, `Maximum ${max} characters allowed in ${message}`)
    .matches(matches, `Only valid characters are allowed in ${message}`)
    .required(required ? `${message} is required` : undefined);

const validations = {
  optionalString: Yup.string(),
  accountNumber: Yup.string()
    .matches(/^\d{1,15}$/, "Account number must be numeric")
    .required("Account number is required"),
  routingNumber: Yup.string()
    .matches(
      /^\d{1,10}$/,
      "Routing number must be numeric and between 1 to 10 digits"
    )
    .required("Routing number is required"),
  phoneNumber: Yup.string()
    .matches(/^\d{10,15}$/, "Phone number must be between 10 to 15 digits")
    .required("Phone number is required"),
  address: Yup.string()
    .trim()
    .required("Address is required")
    .min(5, "Address must be at least 5 characters")
    .max(200, "Address must be less than 200 characters"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  zipCode: Yup.string()
    .matches(/^\d{5}$/, "Zip code must be 5 digits")
    .required("Zip code is required"),
  ssnLast4: Yup.string()
    .matches(/^\d{4}$/, "SSN must be last 4 digits")
    .required("SSN Last 4 is required"),
  dobDay: Yup.number()
    .min(1, "Invalid day")
    .max(31, "Invalid day")
    .required("Day is required"),
  dobMonth: Yup.number()
    .min(1, "Invalid month")
    .max(12, "Invalid month")
    .required("Month is required"),
  dobYear: Yup.number()
    .min(1900, "Invalid year")
    .max(new Date().getFullYear() - 18, "Must be at least 18 years old")
    .required("Year is required"),
};

export const settlementSchema = Yup.object().shape({
  // Step 1: Personal
  firstName: nameValidation(50, /^[a-zA-Z\s]+$/, true, "First Name"),
  lastName: nameValidation(50, /^[a-zA-Z\s]+$/, true, "Last Name"),
  email: validations.email,
  phoneNumber: validations.phoneNumber,
  dobDay: validations.dobDay,
  dobMonth: validations.dobMonth,
  dobYear: validations.dobYear,

  // Step 2: Bank
  routingNumber: validations.routingNumber,
  accountName: nameValidation(50, /^[a-zA-Z\s]+$/, true, "Account Name"),
  nickName: nameValidation(50, /^[a-zA-Z\s]+$/, true, "Nick Name"),
  bankId: nameValidation(50, undefined, true, "Bank Name"),
  accountNumber: validations.accountNumber,

  // Step 3: Address & SSN
  ssnLast4: validations.ssnLast4,
  address: validations.address,
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required"),
  zipCode: validations.zipCode,
});